import { DateService, PulseEnvironment, PulseNavigator } from '@pulse/browser-shared';
import { RUMEntry, RUMFilter } from '@pulse/common';
import { Observable, ReplaySubject, merge } from 'rxjs';
import { filter, first, map, switchMap } from 'rxjs/operators';
import { FetchCollectorService, RequestStats, XHRCollectorService } from './collectors';
import { rumEntryMatch } from './match';
import { PerformanceTimelineService } from './performance-timeline.service';

interface RequestEntriesPair {
    request?: RequestStats;
    entry: RUMEntry;
}

export class RequestMonitorService {
    private performanceTimeline: Observable<RUMEntry>;

    constructor(
        private fetchCollector: FetchCollectorService,
        private xhrCollector: XHRCollectorService,
        private timeline: PerformanceTimelineService,
        private date: DateService,
        private env: PulseEnvironment,
    ) {
        const replay = new ReplaySubject<RUMEntry>(1000);
        this.performanceTimeline = replay;

        this.timeline.subscribe(['resource']).subscribe(replay);
    }

    subscribe(filters: RUMFilter[]) {
        const start = this.date.hrt();

        const fetchStream = merge(
            this.fetchCollector.requests(),
            this.xhrCollector.requests(),
        ).pipe(switchMap((request) => this.findEntry(request)));

        const others = this.performanceTimeline.pipe(
            filter(
                (i) =>
                    (i.initiator_type !== 'fetch' && i.initiator_type !== 'xhr') ||
                    i.start_time <= start,
            ),
            map((e): RequestEntriesPair => ({ entry: e })),
        );

        return merge(others, fetchStream).pipe(
            map((pair) => this.mergeRequestEntry(pair)),
            filter(Boolean),
            filter((e) => !filters || filters.some((f) => rumEntryMatch(e, f))),
        );
    }

    private mergeRequestEntry(pair: RequestEntriesPair): RUMEntry | null {
        const request = pair.request;
        const bodyAccessible =
            Boolean(request) &&
            request !== undefined &&
            (request.responseMode === 'basic' ||
                request.responseMode === 'cors' ||
                request.responseMode === 'default');

        let entry = pair.entry;

        if (!entry) {
            if (!request) {
                return null;
            }

            const connection = (this.env.win.navigator as PulseNavigator).connection as any;

            entry = {
                name: request.url,
                entry_type: 'resource',
                initiator_type: request.initiator,
                start_time: request.start,
                end_time: request.end,
                task_duration: (request.fetchEnd || request.end) - request.start,
                time_origin: this.date.hrtTimeOrigin,
                response_entry_matched: false,
            };

            if (bodyAccessible) {
                entry.decoded_body_size = request.responseBodySize;
                entry.transfer_size = request.responseBodySize;
            }

            if (connection) {
                entry = {
                    ...entry,
                    connection_downlink: connection.downlink,
                    connection_rtt: connection.rtt,
                    connection_type: connection.effectiveType,
                    data_saving_mode: connection.saveData,
                };
            }
        } else {
            entry.response_entry_matched = Boolean(request);
        }

        if (request) {
            this.enrichWithRequest(entry, request, bodyAccessible);
        }

        return entry;
    }

    private enrichWithRequest(entry: RUMEntry, request: RequestStats, bodyAccessible: boolean) {
        entry.request_method = request.method;
        entry.response_status = request.responseStatus;
        entry.response_mode = request.responseMode;
        entry.request_body_size = request.requestBodySize;
        entry.response_end = entry.response_end || request.fetchEnd || request.end;
        entry.end_time = request.end;

        entry.user_duration = request.end - request.start;
        entry.response_content_type = request.responseContentType;
        entry.request_content_type = request.requestContentType;

        entry.redirected = request.redirected;
        entry.aborted = request.aborted;

        if (bodyAccessible) {
            entry.transfer_size = entry.transfer_size || request.responseBodySize;
            entry.response_body_size = request.responseBodySize;
            entry.decoded_body_size = request.responseBodySize;
            entry.transfer_size = request.responseBodySize;
        }
    }

    private findEntry(request: RequestStats): Observable<RequestEntriesPair> {
        return this.performanceTimeline.pipe(
            filter((i) => i.initiator_type === 'fetch' || i.initiator_type === 'xhr'),
            filter((entry) => this.matchLax(request, entry)),
            first(),
            map((entry) => ({ entry, request })),
        );
    }

    private matchLax(request: RequestStats, entry: RUMEntry): boolean {
        const startTime = entry.start_time;
        const endTime = entry.task_duration
            ? entry.start_time + entry.task_duration
            : entry.response_end || 0;

        return (
            startTime >= request.start - 1 &&
            endTime <= request.end + 1 &&
            request.url === entry.name
        );
    }
}
